import type { HeaderSocialNetwork } from './header-types';
import type { ReactElement, SVGProps } from 'react';

import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { cloneElement } from 'react';

type IconProps = SVGProps<SVGSVGElement>;

/*
 * Icons are sized and coloured through attributes only. The global CSS bundle opens with the
 * `@namespace` of default-css.css, so no class selector (Tailwind utilities, preflight) matches
 * an SVG element; their parents lay them out instead.
 */
export function OutlineIcon({ size, strokeWidth, ...props }: IconProps & { size: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      aria-hidden
      {...props}
    />
  );
}

export function PhoneIcon(props: IconProps) {
  return (
    <OutlineIcon size={18} strokeWidth={1.8} {...props}>
      <path d="M5 3h4l2 5-2.5 1.5a11 11 0 0 0 6 6L16 13l5 2v4a2 2 0 0 1-2 2A17 17 0 0 1 3 5a2 2 0 0 1 2-2" />
    </OutlineIcon>
  );
}

export function MenuIcon(props: IconProps) {
  return (
    <OutlineIcon size={24} strokeWidth={2} {...props}>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </OutlineIcon>
  );
}

export function CloseIcon(props: IconProps) {
  return (
    <OutlineIcon size={24} strokeWidth={2} {...props}>
      <path d="M6 6l12 12M18 6 6 18" />
    </OutlineIcon>
  );
}

export function SearchIcon(props: IconProps) {
  return (
    <OutlineIcon size={20} strokeWidth={2} {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </OutlineIcon>
  );
}

export function UserIcon(props: IconProps) {
  return (
    <OutlineIcon size={22} strokeWidth={1.8} {...props}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </OutlineIcon>
  );
}

export function HeartIcon(props: IconProps) {
  return (
    <OutlineIcon size={22} strokeWidth={1.8} {...props}>
      <path d="M12 21s-7.5-4.6-9.5-9.3C1 8 3.4 4.5 7 4.5c2 0 3.6 1.1 5 3 1.4-1.9 3-3 5-3 3.6 0 6 3.5 4.5 7.2C19.5 16.4 12 21 12 21z" />
    </OutlineIcon>
  );
}

export function CartIcon(props: IconProps) {
  return (
    <OutlineIcon size={22} strokeWidth={1.8} {...props}>
      <path d="M3 4h2l2.4 11.2a2 2 0 0 0 2 1.6h7.7a2 2 0 0 0 2-1.5L21 8H6" />
      <circle cx="10" cy="20" r="1.4" />
      <circle cx="17" cy="20" r="1.4" />
    </OutlineIcon>
  );
}

const SOCIAL_PATHS: Partial<Record<HeaderSocialNetwork, string>> = {
  facebook: 'M14 8h3V4h-3c-2.8 0-4.5 1.8-4.5 4.6V11H7v4h2.5v9h4v-9h3l.5-4h-3.5V8.8c0-.5.3-.8.5-.8z',
  youtube:
    'M23 7.2a3 3 0 0 0-2.1-2.1C19 4.6 12 4.6 12 4.6s-7 0-8.9.5A3 3 0 0 0 1 7.2 31 31 0 0 0 .5 12 31 31 0 0 0 1 16.8a3 3 0 0 0 2.1 2.1c1.9.5 8.9.5 8.9.5s7 0 8.9-.5a3 3 0 0 0 2.1-2.1 31 31 0 0 0 .5-4.8 31 31 0 0 0-.5-4.8zM9.8 15.1V8.9l5.8 3.1-5.8 3.1z',
};

/** Networks without a design-specific glyph fall back to the shared eliadmin icon set. */
export function SocialIcon({ network }: { network: HeaderSocialNetwork }) {
  const path = SOCIAL_PATHS[network];

  if (!path) {
    const icon: ReactElement<{ size?: number; fill?: string }> =
      HEADER_CONFIG.socialMediaIcons[network];
    return cloneElement(icon, { size: 18, fill: 'currentColor' });
  }

  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden>
      <path d={path} />
    </svg>
  );
}
